// Lib
import React, { useMemo } from 'react';
import { ConnectedProps, connect, useSelector } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import classNames from 'classnames';
import * as Immutable from 'immutable';

// Components
import ToolbarMeasure from './ToolbarMeasure';
import ToolbarItemsList from './ToolbarItemsList';
import Trash from '../trash/Trash';
import ToolbarDismissButton from './dismissButton/ToolbarDismissButton';
import ToolbarErrorBoundary from './ToolbarErrorBoundary';

// Hooks
import useBodyClass from '../../../utils/react/useBodyClass';
import useToolbarViewportOffset from '../hooks/useToolbarViewportOffset';
import useToolbarStateManager from '../hooks/useToolbarStateManager';
import useDebouncedValue from '../../../utils/react/useDebouncedValue';
import useToolbarTransitionManager from '../hooks/useToolbarTransitionManager';
import useToolbarSize from '../hooks/useToolbarSize';
import { useTiptapActiveEditorSelector } from '../../../components/tiptapEditor/store/tiptapEditorStoreHooks';

// Utils
import { isBoardLike } from '../../../../common/elements/utils/elementTypeUtils';
import editorStoreConnect from '../../../components/editor/store/components/editorStoreConnect';
import { adjustItemCollapseOrderForUserPreferences, collapseItemsToFit } from '../utils/toolbarToolListCollapseUtils';
import { getToolbarHash } from '../utils/toolbarGeneralUtils';
import { getDesktopBaseToolListItems } from '../utils/desktopBaseToolbarUtils';
import { createToolbarList, getSelectedElementTypes } from '../utils/toolbarAvailableToolsUtils';

// Selectors
import { activePopupSelector } from '../../../components/popupPanel/popupOpenSelector';
import { getCurrentUser, getCurrentUserId } from '../../../user/currentUserSelector';
import { getCurrentlyEditingEditorKey } from '../../../element/selection/currentlyEditingSelector';
import { selectCurrentToolbarState } from '../selector/currentToolbarSelector';
import {
    editedOrSelectedElementIdsSelector,
    editedOrSelectedElementsSelector,
    editorHasTextRangeSelectionSelector,
    getOnboardingDisableToolbar,
    selectedChildElementsSelector,
} from '../selector/onboardingToolbarSelector';

// Actions
import { getParentTaskListThunk } from '../../../element/task/taskActions';

// Types
import { ToolbarListType } from '../toolbarTypes';
import { ImMNElement } from '../../../../common/elements/elementModelTypes';

// Styles
import './Toolbar.scss';

const EMPTY_LIST = Immutable.List<string>();

const TOOLBAR_PRIORITIES = [
    ToolbarListType.CREATE_ELEMENT,
    ToolbarListType.SELECTED,
    ToolbarListType.EDITING,
    ToolbarListType.EDITING_RANGE,
];

const mapStateToProps = createStructuredSelector({
    selectedElementIds: editedOrSelectedElementIdsSelector,
    selectedElements: editedOrSelectedElementsSelector,
    selectedChildElements: selectedChildElementsSelector,
    activePopup: activePopupSelector,
    currentUserId: getCurrentUserId,
    currentUser: getCurrentUser,
    editorKey: getCurrentlyEditingEditorKey,
    onboardingDisableToolbar: getOnboardingDisableToolbar,
});

const mapDispatchToProps = (dispatch: Function) => ({
    dispatchGetSelectedParentTaskList: (elementId: string): ImMNElement | undefined =>
        dispatch(getParentTaskListThunk({ elementId })),
});

const mapEditorStateToProps = createStructuredSelector({
    hasEditorSelection: editorHasTextRangeSelectionSelector,
});

const connector = connect(mapStateToProps, mapDispatchToProps);
const editorConnector = editorStoreConnect(mapEditorStateToProps);

type ToolbarContainerProps = {
    currentBoardId: string;
    gridSize: number;
    gridSizeName: string;
    permissions: number;
    blockTemplateInteraction: boolean;
};

type ToolbarProps = ConnectedProps<typeof connector> & ConnectedProps<typeof editorConnector> & ToolbarContainerProps;

const getToolbarDebounceDuration = (
    selectedElements: ToolbarProps['selectedElements'],
    onboardingDisableToolbar: boolean,
): number => {
    // if we're in onboarding, disable toolbar updates
    if (onboardingDisableToolbar) return -1;

    // if the user has a single board selected, debounce for a longer time to prevent flickering when double clicking
    if (selectedElements.size === 1 && selectedElements.every(isBoardLike)) return 300;

    return 100;
};

/**
 * Root component for the toolbar.
 * Responsible for determining the toolbar size, rendering the toolbar items, and managing the toolbar state.
 * */
const Toolbar = (props: ToolbarProps) => {
    const {
        currentUser,
        selectedElements,
        gridSizeName,
        gridSize,
        currentBoardId,
        onboardingDisableToolbar,
        blockTemplateInteraction,
    } = props;

    const toolbarState = useSelector(selectCurrentToolbarState);

    const toolbarContainerRef = React.useRef<HTMLDivElement>(null);
    const offsetTop = useToolbarViewportOffset();

    const { toolBarWidth, availableHeight, adjustedGridSize, setToolbarHeightMeasurement } = useToolbarSize({
        gridSizeName,
    });

    // body class to allow for toolbar size specific styling in specific tools
    const toolbarClassName = `toolbar-size-${adjustedGridSize.name}`;
    useBodyClass(toolbarClassName);

    const hasTiptapEditorSelection = useTiptapActiveEditorSelector((editor) => {
        return editor?.state?.selection ? !editor.state.selection.empty : false;
    });

    // FIXME-Tiptap - We still need hasEditorSelection as its used in TABLE-CELL
    // we can remove it when we support tables in tiptap
    const hasEditorSelection = props.hasEditorSelection || hasTiptapEditorSelection;

    // need to memoize the value here to prevent unnecessary rerenders,
    // otherwise useDebouncedValue will think the props have changed have changed for every render
    const memoizedPropsWithEditorSelection = useMemo(
        () => ({
            ...props,
            hasEditorSelection,
        }),
        [props, hasEditorSelection],
    );

    // add a debounce to the props to prevent unnecessary rerenders & allow for any pending state changes to be applied
    // - -1ms is used for while toolbar is disabled in onboarding (will prevent all updates)
    // - 300ms is used for boards (in case the user is in the process of double clicking to navigate)
    // - 100ms for other elements (to prevent other toolbar flickers)
    const debouncedProps = useDebouncedValue(
        memoizedPropsWithEditorSelection,
        getToolbarDebounceDuration(selectedElements, onboardingDisableToolbar),
    );

    const selectedElementTypes = getSelectedElementTypes(debouncedProps.selectedElements);
    const baseToolLists = useMemo(
        () => getDesktopBaseToolListItems(selectedElementTypes, debouncedProps.editorKey, debouncedProps.currentUser),
        [selectedElementTypes, debouncedProps.editorKey, debouncedProps.currentUser],
    );

    useToolbarStateManager({
        ...debouncedProps,
        defaultToolbarType: ToolbarListType.CREATE_ELEMENT,
        toolbarTypePriorities: TOOLBAR_PRIORITIES,
        baseToolLists,
        initialToolbars: [
            createToolbarList(
                ToolbarListType.CREATE_ELEMENT,
                TOOLBAR_PRIORITIES,
                baseToolLists,
                debouncedProps.selectedElements,
                debouncedProps.selectedChildElements,
                debouncedProps.currentUser,
                31 /* initialize toolbar state with full permissions, so that this is always the root state */,
                debouncedProps.editorKey,
                debouncedProps.hasEditorSelection,
            ),
        ],
    });

    const { renderedToolbars, focusPosition, dismissButtonVisible } = useToolbarTransitionManager(
        toolbarState,
        toolBarWidth,
    );

    if (!toolbarState) return null;

    const { toolbars, focusIndex } = toolbarState;

    const activeToolbar = toolbars[focusIndex];
    const hideToolbar = activeToolbar?.items.length === 0;

    return (
        <div
            id="toolbar"
            className={classNames('Toolbar', {
                disabled: blockTemplateInteraction,
                hidden: hideToolbar,
            })}
            style={
                {
                    top: offsetTop,
                    '--toolbar-width': hideToolbar ? 0 : `${toolBarWidth}px`,
                } as React.CSSProperties
            }
            data-table-util={true /* this prevents blurring of the table cell editor when clicking on the toolbar */}
        >
            <ToolbarMeasure setAvailableHeight={setToolbarHeightMeasurement} />

            <ToolbarDismissButton show={dismissButtonVisible} activeToolbar={activeToolbar} />

            {blockTemplateInteraction && <div className="template-blocker" />}

            <div className="toolbar-window">
                <div
                    ref={toolbarContainerRef}
                    className="tool-list-container"
                    style={{
                        width: renderedToolbars.length * toolBarWidth,
                        transform: `translate3d(${-focusPosition}px, 0, 0)`,
                    }}
                >
                    {renderedToolbars.map((toolList, index) => {
                        toolList.items = adjustItemCollapseOrderForUserPreferences(toolList.items, currentUser);
                        const renderedItems = collapseItemsToFit(
                            toolList.items,
                            availableHeight,
                            adjustedGridSize.name,
                        );

                        // hash of rendered items is the most stable way to identify the toolbar items
                        const renderedToolsId = getToolbarHash(renderedItems).toString();

                        const toolbarHasDismissButton = toolList.type !== ToolbarListType.CREATE_ELEMENT;

                        return (
                            <div
                                className="tool-list"
                                key={toolList.id}
                                style={{
                                    left: index * toolBarWidth,
                                    top: toolbarHasDismissButton ? 'var(--dismiss-button-height)' : 0,
                                }}
                            >
                                <ToolbarItemsList
                                    getComponent={(item) => item.component}
                                    toolListId={renderedToolsId}
                                    items={renderedItems}
                                    currentUser={debouncedProps.currentUser}
                                    currentUserId={debouncedProps.currentUserId}
                                    currentBoardId={debouncedProps.currentBoardId}
                                    gridSize={adjustedGridSize.size}
                                    gridSizeName={adjustedGridSize.name}
                                    permissions={toolList.capturedState?.permissions || 0}
                                    selectedElementIds={toolList.capturedState?.selectedElementIds || EMPTY_LIST}
                                    editorKey={toolList.capturedState?.editorKey}
                                    hasEditorSelection={toolList.capturedState?.hasEditorSelection || false}
                                />
                            </div>
                        );
                    })}
                </div>
            </div>
            <Trash gridSize={gridSize} gridSizeName={gridSizeName} currentBoardId={currentBoardId} />
        </div>
    );
};

const ConnectedToolbar = connector(editorConnector(Toolbar));

const ToolbarContainer = (props: ToolbarContainerProps) => (
    <div className="ToolbarContainer">
        <ToolbarErrorBoundary>
            <ConnectedToolbar {...props} />
        </ToolbarErrorBoundary>
    </div>
);

export default ToolbarContainer;
